/**
 * layout-elements/index.ts — rejestracja elementów layoutu w `LayoutElementRegistry`.
 *
 * Side-effect import: w bootstrapie aplikacji (App.tsx) wywołać
 *   `import '@/components/modules/layout-elements';`
 * by zarejestrować wszystkie elementy strukturalne / kontenery /
 * interaktywne / treść / komunikaty.
 *
 * Status (F12.H):
 *  - T12.H.2 ✅ — 7 elementów strukturalnych (Sidebar/Header/Footer/Navbar/
 *                 Main/Aside/Highlight)
 *  - T12.H.3 ✅ — 6 kontenerów wizualnych (Card/Row/Columns/Grid/Fieldset/Panel)
 *  - T12.H.4 ✅ — interaktywne (Tabs/Accordion/Collapsible/Stepper/Wizard)
 *  - T12.H.5 ✅ — treść statyczna (Heading/Text/RichContent/Quote/List/Code)
 *  - T12.H.6 ✅ — komunikaty inline (Alert info/success/warning/error)
 *  - Dokumenty: linie dokumentu (increment 1) ✅ — DocumentItemsView (read-only,
 *    builder-canvas mock; real render przez contextRenderers, patrz nagłówek
 *    `DocumentItemsViewElement.tsx`)
 *  - F.RL: powiązania rekordów ✅ — RecordLinksView (read-only builder-canvas
 *    mock; real render przez contextRenderers, patrz nagłówek
 *    `RecordLinksViewElement.tsx`)
 *  - Załączniki + podgląd dokumentu (rejestracja elementów) ✅ — AttachmentsView /
 *    DocumentPreviewView (read-only, builder-canvas mock; real render przez
 *    contextRenderers — powstają osobno, patrz nagłówki
 *    `AttachmentsViewElement.tsx` / `DocumentPreviewViewElement.tsx`)
 *  - Historia zmian ✅ — AuditLogView (read-only, builder-canvas mock; real render przez
 *    contextRenderers). Domyka rozjazd: audyt był jedynym widokiem systemowym dostępnym
 *    wyłącznie jako CAŁA zakładka z seeda — teraz tenant stawia go gdziekolwiek na
 *    formularzu, patrz nagłówek `AuditLogViewElement.tsx`
 *  - Dekretacja faktury (rejestracja typu elementu) ✅ — DecreeLinesView (read-only,
 *    builder-canvas mock; real render przez contextRenderers — wpięcie na strony
 *    faktur osobnym krokiem, patrz nagłówek `DecreeLinesViewElement.tsx`)
 *  - Rejestr VAT faktury (rejestracja typu elementu) ✅ — VatRegisterView (read-only,
 *    builder-canvas mock; real render przez contextRenderers, wpięty na strony
 *    faktur zakupu/sprzedaży, patrz nagłówek `VatRegisterViewElement.tsx`)
 *  - Tabela dodatkowa (rejestracja typu elementu) ✅ — AdditionalTableView (read-only,
 *    builder-canvas mock; real render przez contextRenderers `additional_view`,
 *    już wpięty na 7 klas dokumentów + contractors/employees/users; patrz nagłówek
 *    `AdditionalTableViewElement.tsx`). Domyka rozjazd: tabela dodatkowa była dotąd
 *    dostępna wyłącznie jako CAŁA zakładka (typ `additional` z `TabFormModal`).
 *  - Widok SQL / widoki dodatkowe (rejestracja typu elementu) ✅ — AdditionalQueryView
 *    (read-only, builder-canvas mock; real render przez contextRenderers
 *    `additional_query_view`, wpięty na 7 klas dokumentów + contractors/employees —
 *    BEZ users, patrz nagłówek `AdditionalQueryViewElement.tsx`). Mirror tabeli
 *    dodatkowej — ten sam rozjazd, ta sama naprawa.
 *  - Historia procesu akceptacji (rejestracja typu elementu, F17 Commit B) ✅ —
 *    ProcessView (read-only, builder-canvas mock; real render przez contextRenderers
 *    `process_view`, montuje `ProcessHistoryTab`, wpięty na 7 klas dokumentów +
 *    contractors + moduły własne tenanta (`CUSTOM_MODULE_PALETTE_SCOPE`; render modułów
 *    własnych w `RecordFormPage`), patrz nagłówek `ProcessViewElement.tsx`). Mirror tabeli
 *    dodatkowej — seedowana zakładka `approvals` NIE jest wycofana (osobny commit).
 *  - Przycisk akceptacji (rejestracja typu elementu) ✅ — ProcessActionButton (read-only
 *    zaślepka w canvasie; real render przez contextRenderers `process_action_button`: „Wyślij do
 *    akceptacji” / „Akceptacja” jak w pasku nad zakładkami; ci sami gospodarze co
 *    `process_view` — wspólna lista `PROCESS_ELEMENT_ALLOWED_MODULES`; patrz nagłówek
 *    `ProcessActionButtonElement.tsx`).
 *  - Przycisk SQL (rejestracja typu elementu) ✅ — SqlButton (`sql_button`): napis +
 *    procedura „Na przycisk” kartoteki w ustawieniach klocka; w canvasie sam przycisk (ze
 *    znacznikiem „Procedura usunięta”), real render przez contextRenderers `sql_button` strony
 *    rekordu. Kreator tylko kontrahentów — kolejne kartoteki to wiersz w
 *    `lib/record-procedures/sql-button-hosts.ts`; patrz nagłówek `SqlButtonElement.tsx`.
 *  - Użytkownicy firmy (rejestracja typu elementu) ✅ — CompanyMembersView (read-only
 *    builder-canvas mock; real render przez contextRenderers `company_members_view`,
 *    montuje `CompanyMembersSection`, WYŁĄCZNIE na `companies` — pierwsza kartoteka
 *    spoza dokumentów/kontrahenta/pracownika/usera z tym mechanizmem, patrz nagłówek
 *    `CompanyMembersViewElement.tsx`). Zastępuje pomysł kolejnej zaszytej na sztywno
 *    zakładki (jak `Konta bankowe`/`Działy`) elementem usuwalnym/przywracalnym z
 *    poziomu kreatora, bez developera.
 *  - Widget danych na formularzu (Faza 23, Etap 3 T2) ✅ — WidgetView (`widget_view`), osadza
 *    ŻYWY widget z kreatora widgetów na formularzu kartoteki (kontrahenci/firmy/pracownicy).
 *    W builderze makieta bez danych; na realnej stronie fetch przez `WidgetFormBlock` +
 *    wspólny render wyniku `WidgetResultView` (dzielony z kaflem pulpitu `CustomWidget`),
 *    patrz nagłówek `WidgetViewElement.tsx`.
 *
 * @see docs/phases/PHASE_12H_PLAN.md
 */

import { LayoutElementRegistry } from '@/components/fields/LayoutElementRegistry';

// Interaktywne (T12.H.4) — 5 elementów
import { AccordionElement } from './AccordionElement';
import { AdditionalQueryViewElement } from './AdditionalQueryViewElement';
import { AdditionalTableViewElement } from './AdditionalTableViewElement';
// Komunikaty inline (T12.H.6) — 1 element z 4 wariantami
import { AlertElement } from './AlertElement';
import { AttachmentsViewElement } from './AttachmentsViewElement';
import { AuditLogViewElement } from './AuditLogViewElement';
// Kontenery wizualne (T12.H.3) — 6 elementów
import { CardElement } from './CardElement';
// Treść statyczna (T12.H.5) — 6 elementów
import { CodeElement } from './CodeElement';
import { CollapsibleElement } from './CollapsibleElement';
import { ColumnsElement } from './ColumnsElement';
import { CompanyAddressesViewElement } from './CompanyAddressesViewElement';
import { CompanyBankAccountsViewElement } from './CompanyBankAccountsViewElement';
import { CompanyMembersViewElement } from './CompanyMembersViewElement';
import { ContractorAddressesViewElement } from './ContractorAddressesViewElement';
import { ContractorBankAccountsViewElement } from './ContractorBankAccountsViewElement';
import { ContractorCompanyAccessViewElement } from './ContractorCompanyAccessViewElement';
import { ContractorContactsViewElement } from './ContractorContactsViewElement';
import { ContractorPrimaryAddressViewElement } from './ContractorPrimaryAddressViewElement';
import { DecreeLinesViewElement } from './DecreeLinesViewElement';
import { DocumentItemsViewElement } from './DocumentItemsViewElement';
import { DocumentPreviewViewElement } from './DocumentPreviewViewElement';
import { EmployeeAccountViewElement } from './EmployeeAccountViewElement';
import { EmployeeAddressesViewElement } from './EmployeeAddressesViewElement';
import {
  CommercialAuditViewElement,
  CompanyAuditViewElement,
  ContractAuditViewElement,
  ContractorAuditViewElement,
  ContractorContactAuditViewElement,
  EmployeeAuditViewElement,
  InternalAuditViewElement,
  LetterAuditViewElement,
  ProductAuditViewElement,
  PurchaseInvoiceAuditViewElement,
  SaleInvoiceAuditViewElement,
  SpecjalneAuditViewElement,
  TaskAuditViewElement,
  UserAuditViewElement,
} from './EntityAuditViewElements';
import { FieldsetElement } from './FieldsetElement';
import { GridElement } from './GridElement';
import { HeadingElement } from './HeadingElement';
// Struktura strony (T12.H.2) — 7 elementów
import { HighlightElement } from './HighlightElement';
import { ListElement } from './ListElement';
import { PageAsideElement } from './PageAsideElement';
import { PageFooterElement } from './PageFooterElement';
import { PageHeaderElement } from './PageHeaderElement';
import { PageMainElement } from './PageMainElement';
import { PageNavbarElement } from './PageNavbarElement';
import { PanelElement } from './PanelElement';
import { ProcessActionButtonElement } from './ProcessActionButtonElement';
import { ProcessViewElement } from './ProcessViewElement';
import { QuoteElement } from './QuoteElement';
import { RecordLinksViewElement } from './RecordLinksViewElement';
import { RichContentElement } from './RichContentElement';
import { RowElement } from './RowElement';
import { SidebarElement } from './SidebarElement';
import { SqlButtonElement } from './SqlButtonElement';
import { StatCardGroupElement } from './StatCardGroupElement';
import { StepperElement } from './StepperElement';
import { TabsElement } from './TabsElement';
import { TextElement } from './TextElement';
import { UserCompanyAccessViewElement } from './UserCompanyAccessViewElement';
import { UserSecurityViewElement } from './UserSecurityViewElement';
import { VatRegisterViewElement } from './VatRegisterViewElement';
import { WidgetViewElement } from './WidgetViewElement';
import { WizardElement } from './WizardElement';

// ─── Side-effect rejestracja ────────────────────────────────────────────────

// Struktura
LayoutElementRegistry.register(HighlightElement);
LayoutElementRegistry.register(PageHeaderElement);
LayoutElementRegistry.register(PageFooterElement);
LayoutElementRegistry.register(PageNavbarElement);
LayoutElementRegistry.register(PageMainElement);
LayoutElementRegistry.register(PageAsideElement);
LayoutElementRegistry.register(SidebarElement);

// Kontenery
LayoutElementRegistry.register(CardElement);
LayoutElementRegistry.register(RowElement);
LayoutElementRegistry.register(ColumnsElement);
LayoutElementRegistry.register(GridElement);
LayoutElementRegistry.register(FieldsetElement);
LayoutElementRegistry.register(PanelElement);

// Interaktywne
LayoutElementRegistry.register(TabsElement);
LayoutElementRegistry.register(AccordionElement);
LayoutElementRegistry.register(CollapsibleElement);
LayoutElementRegistry.register(StepperElement);
LayoutElementRegistry.register(WizardElement);

// Treść statyczna
LayoutElementRegistry.register(HeadingElement);
LayoutElementRegistry.register(TextElement);
LayoutElementRegistry.register(RichContentElement);
LayoutElementRegistry.register(QuoteElement);
LayoutElementRegistry.register(ListElement);
LayoutElementRegistry.register(CodeElement);
LayoutElementRegistry.register(DocumentItemsViewElement);
LayoutElementRegistry.register(CompanyMembersViewElement);
LayoutElementRegistry.register(AdditionalTableViewElement);
LayoutElementRegistry.register(AdditionalQueryViewElement);
LayoutElementRegistry.register(DecreeLinesViewElement);
LayoutElementRegistry.register(DocumentPreviewViewElement);
LayoutElementRegistry.register(AttachmentsViewElement);
LayoutElementRegistry.register(AuditLogViewElement);
LayoutElementRegistry.register(StatCardGroupElement);
LayoutElementRegistry.register(WidgetViewElement);
LayoutElementRegistry.register(RecordLinksViewElement);
LayoutElementRegistry.register(VatRegisterViewElement);
LayoutElementRegistry.register(ProcessViewElement);
LayoutElementRegistry.register(ProcessActionButtonElement);
LayoutElementRegistry.register(SqlButtonElement);
// Bloki kartoteki Kontrahenci (konfigurowalne zakładki) — scoped allowedModules:['contractors']
LayoutElementRegistry.register(ContractorContactsViewElement);
LayoutElementRegistry.register(ContractorAddressesViewElement);
LayoutElementRegistry.register(ContractorBankAccountsViewElement);
LayoutElementRegistry.register(ContractorCompanyAccessViewElement);
// Adres macierzysty jako jedna formatka (Model B) — zastępuje 7 osobnych pól adresu.
LayoutElementRegistry.register(ContractorPrimaryAddressViewElement);
// Bloki kartoteki Pracownicy (konfigurowalne zakładki) — scoped allowedModules:['employees']
LayoutElementRegistry.register(EmployeeAddressesViewElement);
LayoutElementRegistry.register(EmployeeAccountViewElement);
// Bloki kartoteki Firmy (konfigurowalne zakładki) — scoped allowedModules:['companies']
LayoutElementRegistry.register(CompanyAddressesViewElement);
LayoutElementRegistry.register(CompanyBankAccountsViewElement);
// „Historia zmian" PER KARTOTEKA — osobny element/typ dla każdej kartoteki (jak adresy),
// zamiast wspólnego `audit_log_view`. Read-side per kartoteka na stronie detalu.
LayoutElementRegistry.register(ContractorAuditViewElement);
LayoutElementRegistry.register(EmployeeAuditViewElement);
LayoutElementRegistry.register(CompanyAuditViewElement);
LayoutElementRegistry.register(UserAuditViewElement);
// „Pola dodatkowe" kontrahentów / pracowników / firm / użytkowników / kontaktów to NIE blok
// palety — żyją w zakładce „Dane" jako pojedyncze pola ułożone w kreatorze.
// Blok kartoteki Kontakty kontrahentów (konfigurowalne zakładki) — historia zmian.
LayoutElementRegistry.register(ContractorContactAuditViewElement);
// Bloki kartoteki Użytkownicy (konfigurowalne zakładki) — panele: dostęp do firm + bezpieczeństwo.
// Read-side na UserDetail przez contextRenderers; scoped allowedModules:['users'].
LayoutElementRegistry.register(UserCompanyAccessViewElement);
// dokument-blokify (2026-08-23/24): Audit log klas dokumentów jako przenośne bloki per klasa
// (jak wyżej dla kartotek). „Pola dodatkowe" klas dokumentów to NIE blok palety — pole dodatkowe
// pokazuje się tylko tam, gdzie ktoś postawił je w układzie.
LayoutElementRegistry.register(PurchaseInvoiceAuditViewElement);
LayoutElementRegistry.register(SaleInvoiceAuditViewElement);
LayoutElementRegistry.register(ContractAuditViewElement);
LayoutElementRegistry.register(LetterAuditViewElement);
LayoutElementRegistry.register(CommercialAuditViewElement);
LayoutElementRegistry.register(InternalAuditViewElement);
LayoutElementRegistry.register(SpecjalneAuditViewElement);
// kartoteka-blokify (2026-08-27): Towary — Audit log jako przenośny blok (parytet z pozostałymi
// kartotekami). scoped allowedModules:['products']. „Pola dodatkowe" Towarów to NIE blok palety
// (jak w pozostałych kartotekach) — pole dodatkowe pokazuje się tylko tam, gdzie ktoś postawił je
// w układzie jako zwykłe pole.
LayoutElementRegistry.register(ProductAuditViewElement);
// Kartoteka Zadania: „Historia zmian" jako przenośny blok (scoped allowedModules:['tasks']).
LayoutElementRegistry.register(TaskAuditViewElement);
LayoutElementRegistry.register(UserSecurityViewElement);

// Komunikaty inline
LayoutElementRegistry.register(AlertElement);

// Re-export dla bezpośrednich importów (np. w testach lub innych modułach)
export {
  AccordionElement,
  AdditionalQueryViewElement,
  AdditionalTableViewElement,
  // Komunikaty
  AlertElement,
  AttachmentsViewElement,
  AuditLogViewElement,
  // Kontenery
  CardElement,
  CodeElement,
  CollapsibleElement,
  ColumnsElement,
  CompanyAddressesViewElement,
  CompanyMembersViewElement,
  ContractorAddressesViewElement,
  ContractorBankAccountsViewElement,
  ContractorCompanyAccessViewElement,
  ContractorContactsViewElement,
  ContractorPrimaryAddressViewElement,
  DecreeLinesViewElement,
  DocumentItemsViewElement,
  DocumentPreviewViewElement,
  EmployeeAccountViewElement,
  EmployeeAddressesViewElement,
  FieldsetElement,
  GridElement,
  // Treść
  HeadingElement,
  // Struktura
  HighlightElement,
  ListElement,
  PageAsideElement,
  PageFooterElement,
  PageHeaderElement,
  PageMainElement,
  PageNavbarElement,
  PanelElement,
  ProcessActionButtonElement,
  ProcessViewElement,
  QuoteElement,
  RecordLinksViewElement,
  RichContentElement,
  RowElement,
  SidebarElement,
  SqlButtonElement,
  StatCardGroupElement,
  StepperElement,
  // Interaktywne
  TabsElement,
  TextElement,
  UserCompanyAccessViewElement,
  UserSecurityViewElement,
  VatRegisterViewElement,
  WidgetViewElement,
  WizardElement,
};
